import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import ReactSelect from "react-select";
import { CSVLink } from "react-csv";
import { brandPalette } from "../../../constants/brandPalette";
import CTable from "../../../components/Table/table";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { getAbsentStudentsRequest } from "../../../modules/admin-modules/reports/Actions";
import { getAllLecturesRequest } from "../../../modules/admin-modules/lectures/Actions";
import { getAllGroupsRequest } from "../../../modules/admin-modules/groups/Actions";

const focusRing = "0 0 0 1px rgba(91, 13, 22, 0.22)";
const selectBase = adminSelectStyles({ menuZIndex: 1600 });
const selectStyles = {
  ...selectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...selectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};
const cardSx = {
  border: "1px solid",
  borderColor: "var(--border-subtle)",
  bg: "var(--surface-raised)",
  borderRadius: "18px",
  boxShadow: "0 18px 55px rgba(0,0,0,0.12)",
  color: "var(--text-primary)",
};

const headers = [
  { label: "name", key: "name" },
  { label: "user_name", key: "user_name" },
  { label: "phone", key: "phone" },
  { label: "parent_phone", key: "parent_phone" },
];

const columns = [
  { Header: "#", accessor: "index" },
  { Header: "Student", accessor: "name" },
  { Header: "Code", accessor: "user_name" },
  { Header: "Parent phone", accessor: "parent_phone" },
  { Header: "Phone", accessor: "phone" },
];

const AbsentReport = () => {
  const dispatch = useDispatch();
  const [lecId, setLecId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [lectureDate, setLectureDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [pageNumber, setPageNumber] = useState(0);
  const [size, setPerPage] = useState(10);
  const setPage = () => {};

  useEffect(() => {
    dispatch(getAllLecturesRequest());
    dispatch(getAllGroupsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (lecId && groupId && lectureDate) {
      dispatch(
        getAbsentStudentsRequest({
          lec_id: lecId,
          group_id: groupId,
          lecture_date: lectureDate,
        })
      );
    }
  }, [lecId, groupId, lectureDate, dispatch]);

  const lectures = useSelector((state) => state.lectures.lectures);
  const groups = useSelector((state) => state.groups.groups);
  const absentStudents = useSelector((state) => state.reports.absentStudents);
  const isLoading = useSelector((state) => state.reports.isLoading);

  const lectureOptions = useMemo(
    () =>
      (lectures || []).map((data) => ({
        value: data.id,
        label: data.title,
      })),
    [lectures]
  );

  const groupOptions = useMemo(
    () =>
      (groups || []).map((data) => ({
        value: data.id,
        label: data.name,
      })),
    [groups]
  );

  const Data = useMemo(() => {
    const list = Array.isArray(absentStudents) ? absentStudents : [];
    return list.map((data, index) => ({
      ...data,
      index: `#${index + 1}`,
      id: data?.id,
      name: data?.name,
      user_name: data?.user_name,
      parent_phone: data?.parent_phone,
      phone: data?.phone,
    }));
  }, [absentStudents]);

  const csvData = Data.map((row) => ({
    ...row,
    phone: row.phone != null ? `\t${String(row.phone)}` : "",
    parent_phone: row.parent_phone != null ? `\t${String(row.parent_phone)}` : "",
  }));

  const canLoad = Boolean(lecId && groupId && lectureDate);

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Absence report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Students who did not attend the selected center session.
            </Text>
          </Box>
          <LogoutMenu />
        </Flex>

        <Flex gap={4} mb={6} flexWrap="wrap" align={{ base: "stretch", md: "flex-end" }}>
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Center
            </Text>
            <ReactSelect
              options={lectureOptions}
              isMulti={false}
              onChange={(opt) => setLecId(opt?.value ?? "")}
              styles={selectStyles}
              placeholder="Select center"
              value={lectureOptions.find((o) => o.value === lecId) || null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>
          <Box flex="1" minW={{ base: "100%", md: "220px" }} maxW={{ md: "320px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Group
            </Text>
            <ReactSelect
              options={groupOptions}
              isMulti={false}
              onChange={(opt) => setGroupId(opt?.value ?? "")}
              styles={selectStyles}
              placeholder="Select group"
              value={groupOptions.find((o) => o.value === groupId) || null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              isClearable
            />
          </Box>
          <Box minW={{ base: "100%", md: "200px" }} maxW={{ md: "240px" }}>
            <Text fontSize="xs" fontWeight="800" color="var(--text-muted)" mb={2} textTransform="uppercase">
              Date
            </Text>
            <Input
              type="date"
              value={lectureDate}
              onChange={(e) => setLectureDate(e.target.value)}
              borderRadius="14px"
              borderColor="var(--border-subtle)"
              bg="var(--surface-muted)"
              color="var(--text-primary)"
              fontWeight="600"
              _focus={{ borderColor: brandPalette.primary, boxShadow: focusRing }}
            />
          </Box>
          {Data.length > 0 ? (
            <CSVLink
              headers={headers}
              data={csvData}
              filename={`absence-report-${lectureDate}.csv`}
              target="_blank"
            >
              <Button
                size="sm"
                h="44px"
                borderRadius="lg"
                bg={brandPalette.secondary}
                color="white"
                fontWeight="800"
                px={6}
                _hover={{ bg: brandPalette.primary }}
              >
                Export CSV
              </Button>
            </CSVLink>
          ) : null}
        </Flex>

        <Box {...cardSx} p={{ base: 4, md: 6 }}>
          {!canLoad ? (
            <Text fontWeight="600" color="var(--text-muted)">
              Select a center, group, and date to load absences.
            </Text>
          ) : (
            <CTable
              selectedData={() => {}}
              Columns={columns}
              Data={Data}
              Actions
              currentpage={pageNumber}
              setPage={setPage}
              setPageNumber={setPageNumber}
              setPerPage={setPerPage}
              totalPage={Math.ceil((Data.length || 0) / size) || 1}
              isLoading={isLoading}
              perPage={size}
              noSearchBar
              noFilter
              noSecondSearchBar
              wrapperProps={{
                borderWidth: "1px",
                borderColor: "var(--border-subtle)",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            />
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default AbsentReport;
